/**
 * SKOP API Documentation Page
 *
 * Documentation for the public API.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS, API_RATE_LIMITS, type ApiEndpoint } from '../lib/api-keys';
import { ApiKeyManager } from '../components/ApiKeyManager';
import { useAuth } from '../contexts/AuthContext';

export function ApiDocs() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [copiedExample, setCopiedExample] = useState<string | null>(null);

  const handleCopyExample = async (endpoint: ApiEndpoint) => {
    try {
      await navigator.clipboard.writeText(endpoint.example);
      setCopiedExample(endpoint.path);
      setTimeout(() => setCopiedExample(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (!isAuthenticated) {
    return (
      <div style={{
        paddingTop: '48px',
        textAlign: 'center',
        maxWidth: '400px',
        margin: '0 auto',
      }}>
        <div style={{ width: '48px', height: '48px', margin: '0 auto 16px', color: 'var(--accent)' }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="16 18 22 12 16 6"/>
            <polyline points="8 6 2 12 8 18"/>
          </svg>
        </div>
        <h2 style={{ marginBottom: '8px' }}>API Documentation</h2>
        <p style={{ color: 'var(--text-tertiary)' }}>
          Connect your wallet and sign in to access the API documentation.
        </p>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '16px', maxWidth: '900px', margin: '0 auto', paddingBottom: '48px' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '24px',
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: '8px',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--text-secondary)',
            cursor: 'pointer',
          }}
        >
          ←
        </button>
        <div>
          <h1 style={{
            fontSize: '24px',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            <span style={{ width: '24px', height: '24px', color: 'var(--accent)' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="16 18 22 12 16 6"/>
                <polyline points="8 6 2 12 8 18"/>
              </svg>
            </span>
            API Documentation
          </h1>
          <p style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
            Programmatic access to SKōP token analysis
          </p>
        </div>
      </div>

      {/* API Key Manager */}
      <ApiKeyManager />

      {/* Rate Limits */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
          Rate Limits
        </h2>

        {/* API rate limits */}
        <div style={{
          padding: '16px',
          background: 'rgba(138, 43, 226, 0.08)',
          border: '1px solid #a855f7',
          borderRadius: 'var(--radius-md)',
          marginBottom: '16px',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '12px',
          }}>
            <span style={{ width: '18px', height: '18px', display: 'inline-flex' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#a855f7" strokeWidth="1.5">
                <polyline points="16 18 22 12 16 6"/>
                <polyline points="8 6 2 12 8 18"/>
              </svg>
            </span>
            <span style={{ fontWeight: 600, color: '#a855f7' }}>API Access</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text-tertiary)' }}>Daily limit</span>
              <span style={{ fontWeight: 600 }}>{API_RATE_LIMITS.api.dailyLimit.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text-tertiary)' }}>Per minute</span>
              <span>{API_RATE_LIMITS.api.perMinuteLimit}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text-tertiary)' }}>Per second</span>
              <span>{API_RATE_LIMITS.api.perSecondLimit}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Authentication */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px' }}>
          Authentication
        </h2>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
          Include your API key in the <code style={{
            padding: '2px 6px',
            background: 'var(--bg-tertiary)',
            borderRadius: '4px',
            fontFamily: 'var(--font-mono)',
          }}>Authorization</code> header:
        </p>
        <div style={{
          padding: '12px',
          background: 'var(--bg-tertiary)',
          borderRadius: 'var(--radius-md)',
          fontFamily: 'var(--font-mono)',
          fontSize: '12px',
          overflowX: 'auto',
        }}>
          Authorization: Bearer sk_live_your_api_key_here
        </div>
      </div>

      {/* Endpoints */}
      <div className="card">
        <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
          Endpoints
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {API_ENDPOINTS.map((endpoint) => (
            <EndpointCard
              key={endpoint.path}
              endpoint={endpoint}
              copied={copiedExample === endpoint.path}
              onCopy={() => handleCopyExample(endpoint)}
            />
          ))}
        </div>
      </div>

      {/* Base URL */}
      <div style={{
        marginTop: '24px',
        padding: '16px',
        background: 'var(--bg-tertiary)',
        borderRadius: 'var(--radius-md)',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>
          Base URL
        </div>
        <div style={{
          fontFamily: 'var(--font-mono)',
          fontSize: '14px',
          color: 'var(--accent)',
        }}>
          https://api.skop.site
        </div>
      </div>
    </div>
  );
}

function EndpointCard({
  endpoint,
  copied,
  onCopy,
}: {
  endpoint: ApiEndpoint;
  copied: boolean;
  onCopy: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-md)',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          padding: '12px 16px',
          background: 'var(--bg-tertiary)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <span style={{
          padding: '4px 8px',
          fontSize: '11px',
          fontWeight: 600,
          background: endpoint.method === 'GET'
            ? 'rgba(0, 255, 136, 0.15)'
            : 'rgba(0, 136, 255, 0.15)',
          color: endpoint.method === 'GET' ? '#00ff88' : '#0088ff',
          borderRadius: 'var(--radius-sm)',
        }}>
          {endpoint.method}
        </span>
        <span style={{
          fontFamily: 'var(--font-mono)',
          fontSize: '13px',
          flex: 1,
        }}>
          {endpoint.path}
        </span>
        <span style={{
          fontSize: '12px',
          color: 'var(--text-tertiary)',
          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s',
        }}>
          ▼
        </span>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div style={{ padding: '16px' }}>
          <p style={{
            fontSize: '13px',
            color: 'var(--text-secondary)',
            marginBottom: '16px',
          }}>
            {endpoint.description}
          </p>

          {/* Parameters */}
          {endpoint.parameters && endpoint.parameters.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{
                fontSize: '12px',
                fontWeight: 600,
                color: 'var(--text-tertiary)',
                marginBottom: '8px',
              }}>
                PARAMETERS
              </div>
              <div style={{
                background: 'var(--bg-tertiary)',
                borderRadius: 'var(--radius-md)',
                overflow: 'hidden',
              }}>
                {endpoint.parameters.map((param, i) => (
                  <div
                    key={param.name}
                    style={{
                      padding: '10px 12px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      borderBottom: i < (endpoint.parameters?.length ?? 0) - 1
                        ? '1px solid var(--border)'
                        : undefined,
                    }}
                  >
                    <div style={{ minWidth: '120px' }}>
                      <span style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: '12px',
                      }}>
                        {param.name}
                      </span>
                      {param.required && (
                        <span style={{
                          marginLeft: '4px',
                          color: 'var(--error)',
                          fontSize: '12px',
                        }}>
                          *
                        </span>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <span style={{
                        fontSize: '11px',
                        color: 'var(--accent)',
                        fontFamily: 'var(--font-mono)',
                      }}>
                        {param.type}
                      </span>
                      <span style={{
                        fontSize: '12px',
                        color: 'var(--text-tertiary)',
                        display: 'block',
                        marginTop: '2px',
                      }}>
                        {param.description}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Response */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{
              fontSize: '12px',
              fontWeight: 600,
              color: 'var(--text-tertiary)',
              marginBottom: '8px',
            }}>
              RESPONSE
            </div>
            <div style={{
              padding: '10px 12px',
              background: 'var(--bg-tertiary)',
              borderRadius: 'var(--radius-md)',
              fontSize: '12px',
              color: 'var(--text-secondary)',
            }}>
              {endpoint.response}
            </div>
          </div>

          {/* Example */}
          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px',
            }}>
              <span style={{
                fontSize: '12px',
                fontWeight: 600,
                color: 'var(--text-tertiary)',
              }}>
                EXAMPLE
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onCopy();
                }}
                style={{
                  padding: '4px 8px',
                  fontSize: '11px',
                  background: copied ? 'rgba(0, 255, 136, 0.1)' : 'var(--surface-secondary)',
                  border: '1px solid',
                  borderColor: copied ? '#00ff88' : 'var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  color: copied ? '#00ff88' : 'var(--text-secondary)',
                  cursor: 'pointer',
                }}
              >
                {copied ? '✓ Copied' : 'Copy'}
              </button>
            </div>
            <div style={{
              padding: '12px',
              background: '#1a1a2e',
              borderRadius: 'var(--radius-md)',
              fontFamily: 'var(--font-mono)',
              fontSize: '11px',
              overflowX: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              color: '#e0e0e0',
            }}>
              {endpoint.example}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
